import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Info } from "lucide-react";
import { callApi } from "../../../api/fetch";
import { logout } from "../../../utils/logout";
import RenderTooltip from "../../../Shared/tooltip/tooltip";
import Loading from "../../../Shared/loading/loading";
import { ACCENTCOLOR } from "../../../Shared/colors";
import { testIdProps } from "../../../utils/testIdProps";

type PlanDetailType = {
  loading: boolean;
  planData: any;
  error: string | null;
};

const PlanDetails = ({ loading, planData, error }: PlanDetailType) => {
  const [activeView, setActiveView] = useState<"current" | "next">("current");
  // const [planData, setPlanData] = useState<any>(null);
  // const [loading, setLoading] = useState<boolean>(true);
  // const [error, setError] = useState<string | null>(null);

  // Organization ID from localStorage
  // const orgId = localStorage.getItem("organisation_id");
  // Check if all required authentication data exists
  // if (!orgId) {
  //   localStorage.clear();
  //   logout({ redirectToLogin: true });
  // }

  // Fetch plan data from API
  // const fetchPlanData = async () => {
  //   try {
  //     setLoading(true);
  //     setError(null);

  //     const response = await callApi(
  //       `/organisations/${orgId}?action=get_org_plan_info`,
  //       {
  //         method: "GET",
  //       }
  //     );

  //     setPlanData(response);
  //   } catch (error: any) {
  //     setError(error?.message || "Failed to fetch plan data");
  //     console.error("Plan API Error:", error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Load plan data on component mount
  // useEffect(() => {
  //   if (orgId) {
  //     fetchPlanData();
  //   } else {
  //     setLoading(false);
  //   }
  // }, [orgId]);

  // Loading state
  if (loading) {
    return (
      <div className="px-4">
        <div
          className="bg-white rounded-4 shadow-sm d-flex align-items-center"
          style={{ height: "80px" }}
        >
          <div className="d-flex align-items-center justify-content-center w-100">
            <Loading loadingColor={ACCENTCOLOR} />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-4">
        <div className="bg-white rounded-4 shadow-sm p-4">
          <div className="text-center text-danger">Error: {error}</div>
        </div>
      </div>
    );
  }

  // Check if no plan data exists
  if (!planData || (!planData?.current && !planData?.next)) {
    return (
      <div className="p-4">
        <div className="bg-white rounded-4 shadow-sm d-flex align-items-center">
          <div className="text-center w-100 py-3">
            <div className="d-flex flex-column align-items-center gap-2">
              <div className="fw-medium text-danger">No Plans Found</div>

              <div className="text-muted" style={{ fontSize: "12px" }}>
                Contact <span className="text-primary">support@zoop.one</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hasNextPlan = planData?.next ? true : false;

  const formatBalance = (balance?: number) => {
    return `₹ ${balance?.toLocaleString() || "0"}`;
  };

  const getValidityText = (plan?: any) => {
    if (plan?.validity_in_months) {
      return `${plan.validity_in_months} months`;
    }
    return "NA";
  };

  if (!hasNextPlan) {
    // Single current plan view with same styling as combined layout
    return (
      <div className="px-4">
        <div className="py-3 bg-white rounded-4 shadow-sm overflow-hidden position-relative">
          <div
            className="d-flex align-items-center"
            style={{ minWidth: "100%" }}
          >
            <div
              className="d-flex align-items-center px-5"
              style={{ width: "20%" }}
            >
              <div className="d-flex align-items-center gap-2">
                <h6 className="mb-0 fw-medium text-dark fs-6">Current Plan</h6>
              </div>
            </div>

            <div
              className="d-flex justify-content-between align-items-center"
              style={{ width: "70%" }}
            >
              <div className="text-center" style={{ flex: 1 }}>
                <div
                  className="text-semibold text-secondary mb-1"
                  style={{ fontSize: "12px" }}
                >
                  Plan Name
                </div>
                <div
                  className="fw-medium text-dark"
                  style={{ fontSize: "16px" }}
                >
                  {planData?.current?.plan?.plan_name || "N/A"}
                </div>
              </div>

              <div className="text-center border-start" style={{ flex: 1 }}>
                <div
                  className="text-semibold text-secondary mb-1"
                  style={{ fontSize: "12px" }}
                >
                  Plan Validity
                </div>
                <div
                  className="fw-medium text-dark"
                  style={{ fontSize: "16px" }}
                >
                  {getValidityText(planData?.current?.plan)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4">
      <div className="bg-white rounded-4 shadow-sm overflow-hidden position-relative">
        <div
          className="d-flex"
          style={{
            transition: "transform 0.3s ease",
            transform:
              activeView === "next" ? "translateX(-100%)" : "translateX(0)",
          }}
        >
          {/* Current Plan View */}
          <div
            className="d-flex align-items-center"
            style={{ minWidth: "100%" }}
          >
            <div
              {...testIdProps("plan-details-current-tab")}
              className="d-flex align-items-center cursor-pointer px-5"
              style={{ width: "20%" }}
              onClick={() => setActiveView("current")}
            >
              <h6 className="mb-0 fw-medium text-dark fs-6">Current Plan</h6>
            </div>

            <div
              className="d-flex justify-content-between align-items-center"
              style={{ width: "70%" }}
            >
              <div className="text-center" style={{ flex: 1 }}>
                <div
                  className="text-semibold text-secondary mb-1"
                  style={{ fontSize: "12px" }}
                >
                  Plan Name
                </div>
                <div
                  className="fw-medium text-dark"
                  style={{ fontSize: "16px" }}
                >
                  {planData?.current?.plan?.plan_name || "N/A"}
                </div>
              </div>

              <div className="text-center border-start" style={{ flex: 1 }}>
                <div
                  className="text-semibold text-secondary mb-1"
                  style={{ fontSize: "12px" }}
                >
                  Plan Validity
                </div>
                <div
                  className="fw-medium text-dark"
                  style={{ fontSize: "16px" }}
                >
                  {getValidityText(planData?.current?.plan)}
                </div>
              </div>
            </div>

            <div
              {...testIdProps("plan-details-next-tab")}
              className="d-flex align-items-center justify-content-center cursor-pointer rounded p-2 py-3"
              style={{
                width: "13%",
                backgroundColor:
                  activeView === "current" ? "#EBFFF5" : "transparent",
              }}
              onClick={() => setActiveView("next")}
            >
              {/* <div className="d-flex align-items-center gap-2">
                <ChevronLeft size={18} className="text-muted" />

                <div
                  className="d-flex flex-column text-start"
                  style={{ fontSize: "12px" }}
                >
                  <div className="text-muted">Next Plan</div>
                  <div
                    className="fw-medium text-dark"
                    style={{ fontSize: "16px" }}
                  >
                    {planData?.next?.plan?.plan_name || "N/A"}
                  </div>
                </div>
              </div> */}
              <div className="d-flex align-items-center gap-2 px-3 py-2">
                <ChevronLeft size={18} className="text-muted" />

                <div
                  className="d-flex flex-column text-start"
                  style={{ fontSize: "14px" }}
                >
                  <div className="text-muted">Next Plan</div>
                  <div className="fw-medium text-dark">
                    {planData?.next?.plan?.plan_name || "N/A"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Next Plan View */}
          <div
            className="d-flex align-items-center"
            style={{ minWidth: "100%", backgroundColor: "#EBFFF5" }}
          >
            <div
              {...testIdProps("plan-details-back-to-current")}
              className="d-flex align-items-center cursor-pointer rounded p-2"
              style={{
                width: "13%",
                backgroundColor:
                  activeView === "next" ? "#FFFFFF" : "transparent",
              }}
              onClick={() => setActiveView("current")}
            >
              <div className="d-flex align-items-center gap-2 px-3 py-3">
                <div
                  className="d-flex flex-column text-start"
                  style={{ fontSize: "14px" }}
                >
                  <div className="text-muted">Current Plan</div>
                  <div className="fw-medium text-dark">
                    {planData?.current?.plan?.plan_name || "N/A"}
                  </div>
                </div>

                <ChevronRight size={16} className="text-muted" />
              </div>
            </div>

            <div
              className="d-flex align-items-center px-4"
              style={{ width: "15%" }}
            >
              <h6 className="mb-0 w-medium text-dark">Next Plan</h6>
            </div>

            <div
              className="d-flex justify-content-between align-items-center"
              style={{ width: "70%" }}
            >
              <div className="text-center" style={{ flex: 1 }}>
                <div className="text-muted mb-1" style={{ fontSize: "12px" }}>
                  Plan Name
                </div>
                <div
                  className="fw-medium text-dark"
                  style={{ fontSize: "16px" }}
                >
                  {planData?.next?.plan?.plan_name || "N/A"}
                </div>
              </div>

              {/* <div className="text-center border-start" style={{ flex: 1 }}>
                <div className="text-muted mb-1" style={{ fontSize: "12px" }}>
                  Plan Validity
                </div>
                <div
                  className="fw-medium text-dark"
                  style={{ fontSize: "16px" }}
                >
                  {getValidityText(planData?.next?.plan)}
                </div>
              </div> */}

              <div className="text-center border-start" style={{ flex: 1 }}>
                <div className="text-muted mb-1" style={{ fontSize: "12px" }}>
                  Balance to be added
                </div>
                <div
                  className="fw-medium text-dark"
                  style={{ fontSize: "16px" }}
                >
                  {formatBalance(planData?.next?.balance)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanDetails;
